import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Tooltip,
  Statistic,
  Badge,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { ISalaryRequest, SalaryRequestStatus } from "../../types/salaryRequest.type";
import { salaryRequestApi } from "../../api/salaryRequestApi";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function SalaryRequest() {
  const { authState } = useAuth();
  const [salaryRequests, setSalaryRequests] = useState<ISalaryRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    status: undefined as SalaryRequestStatus | undefined,
    month: undefined as string | undefined,
    year: undefined as number | undefined,
  });

  // Модальные окна
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ISalaryRequest | null>(null);

  // Состояние редактирования
  const [editingItems, setEditingItems] = useState<{ [key: string]: boolean }>({});
  const [editForms, setEditForms] = useState<{ [key: string]: any }>({});

  // Лоадеры
  const [createLoading, setCreateLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState<{ [key: string]: boolean }>({});

  // Формы
  const [createForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();

  // Загрузка данных
  const fetchSalaryRequests = async () => {
    setLoading(true);
    try {
      const response = await salaryRequestApi.getSalaryRequests({
        page,
        limit: pageSize,
        ...filters,
      });
      setSalaryRequests(response.salaryRequests);
      setTotal(response.total);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при загрузке данных");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaryRequests();
  }, [page, pageSize, filters]);

  // Создание запроса
  const handleCreateRequest = async (values: any) => {
    setCreateLoading(true);
    try {
      await salaryRequestApi.createSalaryRequest({
        notes: values.notes,
      });
      
      message.success("Запрос на увеличение зарплаты создан");
      setIsCreateModalOpen(false);
      createForm.resetFields();
      fetchSalaryRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при создании запроса");
    } finally {
      setCreateLoading(false);
    }
  };

  // Просмотр запроса
  const handleViewRequest = (request: ISalaryRequest) => {
    setSelectedRequest(request);
    setIsViewModalOpen(true);
  };

  // Начать редактирование элемента
  const handleStartEdit = (requestId: string, itemIndex: number, item: any) => {
    const key = `${requestId}-${itemIndex}`;
    setEditingItems(prev => ({ ...prev, [key]: true }));
    setEditForms(prev => ({
      ...prev,
      [key]: {
        requestedIncrease: item.requestedIncrease,
        reason: item.reason || "",
      }
    }));
  };

  // Отменить редактирование элемента
  const handleCancelEdit = (requestId: string, itemIndex: number) => {
    const key = `${requestId}-${itemIndex}`;
    setEditingItems(prev => ({ ...prev, [key]: false }));
    setEditForms(prev => {
      const newForms = { ...prev };
      delete newForms[key];
      return newForms;
    });
  };

  // Сохранить изменения элемента
  const handleSaveEdit = async (requestId: string, itemIndex: number) => {
    const key = `${requestId}-${itemIndex}`;
    const formData = editForms[key];
    
    if (!formData) return;

    setEditLoading(prev => ({ ...prev, [key]: true }));
    try {
      await salaryRequestApi.updateSalaryRequestItem(requestId, itemIndex, {
        requestedIncrease: formData.requestedIncrease,
        reason: formData.reason,
      });
      
      message.success("Элемент обновлен");
      setEditingItems(prev => ({ ...prev, [key]: false }));
      setEditForms(prev => {
        const newForms = { ...prev };
        delete newForms[key];
        return newForms;
      });
      fetchSalaryRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при обновлении элемента");
    } finally {
      setEditLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Обновление формы редактирования
  const handleEditFormChange = (requestId: string, itemIndex: number, field: string, value: any) => {
    const key = `${requestId}-${itemIndex}`;
    setEditForms(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      }
    }));
  };

  // Одобрение запроса
  const handleApproveRequest = async (values: any) => {
    if (!selectedRequest) return;
    
    setApproveLoading(true);
    try {
      await salaryRequestApi.approveSalaryRequest(selectedRequest._id, {
        notes: values.notes,
      });
      
      message.success("Запрос одобрен");
      setIsApproveModalOpen(false);
      approveForm.resetFields();
      fetchSalaryRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при одобрении запроса");
    } finally {
      setApproveLoading(false);
    }
  };

  // Отклонение запроса
  const handleRejectRequest = async (values: any) => {
    if (!selectedRequest) return;
    
    setRejectLoading(true);
    try {
      await salaryRequestApi.rejectSalaryRequest(selectedRequest._id, {
        rejectionReason: values.rejectionReason,
        notes: values.notes,
      });
      
      message.success("Запрос отклонен");
      setIsRejectModalOpen(false);
      rejectForm.resetFields();
      fetchSalaryRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при отклонении запроса");
    } finally {
      setRejectLoading(false);
    }
  };

  // Удаление запроса
  const handleDeleteRequest = async (id: string) => {
    setDeleteLoading(id);
    try {
      await salaryRequestApi.deleteSalaryRequest(id);
      message.success("Запрос удален");
      fetchSalaryRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при удалении запроса");
    } finally {
      setDeleteLoading(null);
    }
  };

  // Получить цвет статуса
  const getStatusColor = (status: SalaryRequestStatus) => {
    switch (status) {
      case SalaryRequestStatus.PENDING: return "orange";
      case SalaryRequestStatus.APPROVED: return "green";
      case SalaryRequestStatus.REJECTED: return "red";
      default: return "default";
    }
  };

  // Получить текст статуса
  const getStatusText = (status: SalaryRequestStatus) => {
    switch (status) {
      case SalaryRequestStatus.PENDING: return "Ожидает";
      case SalaryRequestStatus.APPROVED: return "Одобрен";
      case SalaryRequestStatus.REJECTED: return "Отклонен";
      default: return "Неизвестно";
    }
  };

  // Колонки таблицы
  const columns = [
    {
      title: "Месяц",
      dataIndex: "month",
      key: "month",
      render: (month: string) => (
        <Space>
          <CalendarOutlined />
          <Text strong>{month}</Text>
        </Space>
      ),
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (status: SalaryRequestStatus) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={getStatusText(status)}
        />
      ),
    },
    {
      title: "Работники",
      dataIndex: "items",
      key: "itemsCount",
      render: (items: any[]) => (
        <Space>
          <UserOutlined />
          <Text>{items.length}</Text>
        </Space>
      ),
    },
    {
      title: "Общая сумма",
      dataIndex: "totalRequestedAmount",
      key: "totalAmount",
      render: (amount: any) => (
        <Space direction="vertical" size="small">
          <Space>
            <DollarOutlined style={{ color: "#52c41a" }} />
            <Text strong>{amount.USD.toLocaleString()} USD</Text>
          </Space>
          <Space>
            <DollarOutlined style={{ color: "#1890ff" }} />
            <Text>{amount.UZS.toLocaleString()} UZS</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: "Создан",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (createdBy: any, record: ISalaryRequest) => (
        <Space direction="vertical" size="small">
          <Text strong>{createdBy?.username || "Неизвестно"}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {dayjs(record.createdAt).format("DD.MM.YYYY HH:mm")}
          </Text>
        </Space>
      ),
    },
    {
      title: "Примечания",
      key: "notes",
      render: (record: ISalaryRequest) => {
        const notes = [];
        if (record.notes) notes.push({ text: record.notes, color: "#1890ff", label: "Создание" });
        if (record.approvalNotes) notes.push({ text: record.approvalNotes, color: "#52c41a", label: "Одобрение" });
        if (record.rejectionReason) notes.push({ text: record.rejectionReason, color: "#ff4d4f", label: "Отклонение" });
        
        if (notes.length === 0) {
          return <Text type="secondary">Нет примечаний</Text>;
        }
        
        return (
          <Space direction="vertical" size="small">
            {notes.map((note, index) => (
              <Tooltip key={index} title={note.label}>
                <div style={{
                  padding: "4px 8px",
                  backgroundColor: note.color === "#1890ff" ? "#f0f9ff" : 
                                   note.color === "#52c41a" ? "#f6ffed" : "#fff2f0",
                  border: `1px solid ${note.color === "#1890ff" ? "#91d5ff" : 
                                       note.color === "#52c41a" ? "#b7eb8f" : "#ffccc7"}`,
                  borderRadius: "4px",
                  color: note.color,
                  fontSize: "12px",
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  {note.label === "Создание" && <FileTextOutlined />}
                  {note.label === "Одобрение" && <CheckCircleFilled />}
                  {note.label === "Отклонение" && <CloseCircleFilled />}
                  {note.text}
                </div>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Действия",
      key: "actions",
      render: (record: ISalaryRequest) => (
        <Space>
          <Tooltip title="Просмотр">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewRequest(record)}
            />
          </Tooltip>
          
          {record.status === SalaryRequestStatus.PENDING && (
            <>
              <Tooltip title="Одобрить">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  style={{ color: "green" }}
                  loading={approveLoading}
                  onClick={() => {
                    setSelectedRequest(record);
                    setIsApproveModalOpen(true);
                  }}
                />
              </Tooltip>
              <Tooltip title="Отклонить">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  style={{ color: "red" }}
                  loading={rejectLoading}
                  onClick={() => {
                    setSelectedRequest(record);
                    setIsRejectModalOpen(true);
                  }}
                />
              </Tooltip>
              <Popconfirm
                title="Удалить запрос?"
                description="Это действие нельзя отменить"
                onConfirm={() => handleDeleteRequest(record._id)}
                okText="Да"
                cancelText="Нет"
                disabled={deleteLoading === record._id}
              >
                <Tooltip title="Удалить">
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    style={{ color: "red" }}
                    loading={deleteLoading === record._id}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Фильтры
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // Рендер элемента списка для просмотра
  const renderViewItem = (item: any, index: number) => (
    <List.Item>
      <List.Item.Meta
        title={
          <Space>
            <UserOutlined />
            <Text strong>{`${item.workerName} ${item.workerSurname || ""}`}</Text>
          </Space>
        }
                 description={
           <Space direction="vertical" size="small">
             <Space>
               <Text type="secondary">Текущая зарплата:</Text>
               <Text strong>{item.currentSalary.toLocaleString()} {item.salaryCurrency}</Text>
             </Space>
             <Space>
               <Text type="secondary">Текущий долг:</Text>
               <Text strong style={{ color: "#faad14" }}>{item.currentDebt.toLocaleString()} {item.salaryCurrency}</Text>
             </Space>
             <Space>
               <Text type="secondary">Запрошенное увеличение:</Text>
               <Text strong style={{ color: "#52c41a" }}>
                 {item.requestedIncrease.toLocaleString()} {item.salaryCurrency}
               </Text>
             </Space>
             <Space>
               <Text type="secondary">Общее увеличение:</Text>
               <Text strong style={{ color: "#1890ff" }}>
                 {(item.totalIncrease || item.currentDebt + item.requestedIncrease).toLocaleString()} {item.salaryCurrency}
               </Text>
             </Space>
             {item.reason && (
               <Space>
                 <Text type="secondary">Причина:</Text>
                 <Text>{item.reason}</Text>
               </Space>
             )}
           </Space>
         }
      />
    </List.Item>
  );

  // Рендер элемента списка для редактирования
  const renderEditItem = (item: any, index: number, requestId: string) => {
    const key = `${requestId}-${index}`;
    const isEditing = editingItems[key];
    const formData = editForms[key] || {};

    if (isEditing) {
      return (
        <Card
          key={index}
          size="small"
          style={{ marginBottom: "16px", border: "1px solid #1890ff" }}
          title={
            <Space>
              <UserOutlined />
              <Text strong>{`${item.workerName} ${item.workerSurname || ""}`}</Text>
            </Space>
          }
          extra={
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                loading={editLoading[key]}
                onClick={() => handleSaveEdit(requestId, index)}
              >
                Сохранить
              </Button>
              <Button
                size="small"
                icon={<CloseCircleOutlined />}
                disabled={editLoading[key]}
                onClick={() => handleCancelEdit(requestId, index)}
              >
                Отмена
              </Button>
            </Space>
          }
        >
                     <Row gutter={16}>
             <Col span={8}>
               <Space direction="vertical" style={{ width: "100%" }}>
                 <Text type="secondary">Текущая зарплата:</Text>
                 <Text strong>{item.currentSalary.toLocaleString()} {item.salaryCurrency}</Text>
               </Space>
             </Col>
             <Col span={8}>
               <Space direction="vertical" style={{ width: "100%" }}>
                 <Text type="secondary">Текущий долг:</Text>
                 <Text strong style={{ color: "#faad14" }}>{item.currentDebt.toLocaleString()} {item.salaryCurrency}</Text>
               </Space>
             </Col>
             <Col span={8}>
               <Space direction="vertical" style={{ width: "100%" }}>
                 <Text type="secondary">Запрошенное увеличение:</Text>
                 <InputNumber
                   value={formData.requestedIncrease}
                   onChange={(value) => handleEditFormChange(requestId, index, "requestedIncrease", value)}
                   min={0}
                   style={{ width: "100%" }}
                 />
               </Space>
             </Col>
           </Row>
           <Row style={{ marginTop: "8px" }}>
             <Col span={24}>
               <Space direction="vertical" style={{ width: "100%" }}>
                 <Text type="secondary">Общее увеличение (долг + запрос):</Text>
                 <Text strong style={{ color: "#1890ff", fontSize: "16px" }}>
                   {(item.currentDebt + (formData.requestedIncrease || 0)).toLocaleString()} {item.salaryCurrency}
                 </Text>
               </Space>
             </Col>
           </Row>
          <div style={{ marginTop: "16px" }}>
            <Text type="secondary">Причина:</Text>
            <TextArea
              value={formData.reason}
              onChange={(e) => handleEditFormChange(requestId, index, "reason", e.target.value)}
              rows={2}
              style={{ marginTop: "8px" }}
            />
          </div>
        </Card>
      );
    }

    return (
      <Card
        key={index}
        size="small"
        style={{ marginBottom: "16px" }}
        title={
          <Space>
            <UserOutlined />
            <Text strong>{`${item.workerName} ${item.workerSurname || ""}`}</Text>
          </Space>
        }
        extra={
          selectedRequest?.status === SalaryRequestStatus.PENDING && 
          (authState.user?.role === UserRole.Admin || authState.user?.role === UserRole.Accountant) ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              loading={editLoading[`${requestId}-${index}`]}
              onClick={() => handleStartEdit(requestId, index, item)}
            >
              Редактировать
            </Button>
          ) : null
        }
      >
                 <Row gutter={16}>
           <Col span={8}>
             <Space direction="vertical" size="small">
               <Text type="secondary">Текущая зарплата:</Text>
               <Text strong>{item.currentSalary.toLocaleString()} {item.salaryCurrency}</Text>
             </Space>
           </Col>
           <Col span={8}>
             <Space direction="vertical" size="small">
               <Text type="secondary">Текущий долг:</Text>
               <Text strong style={{ color: "#faad14" }}>
                 {item.currentDebt.toLocaleString()} {item.salaryCurrency}
               </Text>
             </Space>
           </Col>
           <Col span={8}>
             <Space direction="vertical" size="small">
               <Text type="secondary">Запрошенное увеличение:</Text>
               <Text strong style={{ color: "#52c41a" }}>
                 {item.requestedIncrease.toLocaleString()} {item.salaryCurrency}
               </Text>
             </Space>
           </Col>
         </Row>
         <Row style={{ marginTop: "8px" }}>
           <Col span={24}>
             <Space direction="vertical" size="small">
               <Text type="secondary">Общее увеличение (долг + запрос):</Text>
                                <Text strong style={{ color: "#1890ff", fontSize: "16px" }}>
                   {(item.totalIncrease || item.currentDebt + item.requestedIncrease).toLocaleString()} {item.salaryCurrency}
                 </Text>
             </Space>
           </Col>
         </Row>
        {item.reason && (
          <div style={{ marginTop: "16px" }}>
            <Text type="secondary">Причина:</Text>
            <Text style={{ display: "block", marginTop: "4px" }}>{item.reason}</Text>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div style={{ padding: "24px" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: "24px" }}>
        <Title level={2}>
          <DollarOutlined style={{ marginRight: "8px" }} />
          Запросы на увеличение зарплаты
        </Title>
        
        {authState.user?.role === UserRole.Admin || authState.user?.role === UserRole.Accountant ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalOpen(true)}
            size="large"
            loading={loading}
          >
            Создать запрос на {dayjs().format("MM.YYYY")}
          </Button>
        ) : null}
      </Row>

      {/* Статистика */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Всего запросов"
              value={total}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Ожидают"
              value={salaryRequests.filter(r => r.status === SalaryRequestStatus.PENDING).length}
              valueStyle={{ color: "#faad14" }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Одобрены"
              value={salaryRequests.filter(r => r.status === SalaryRequestStatus.APPROVED).length}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Отклонены"
              value={salaryRequests.filter(r => r.status === SalaryRequestStatus.REJECTED).length}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Фильтры */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={16}>
          <Col span={6}>
            <Select
              placeholder="Статус"
              allowClear
              style={{ width: "100%" }}
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
            >
              <Option value={SalaryRequestStatus.PENDING}>Ожидает</Option>
              <Option value={SalaryRequestStatus.APPROVED}>Одобрен</Option>
              <Option value={SalaryRequestStatus.REJECTED}>Отклонен</Option>
            </Select>
          </Col>
          <Col span={6}>
            <DatePicker
              placeholder="Месяц"
              picker="month"
              style={{ width: "100%" }}
              onChange={(date) => {
                if (date) {
                  handleFilterChange("month", date.format("MM.YYYY"));
                  handleFilterChange("year", date.year());
                } else {
                  handleFilterChange("month", undefined);
                  handleFilterChange("year", undefined);
                }
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Таблица */}
      <Table
        columns={columns}
        dataSource={salaryRequests}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          },
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
        }}
      />

      {/* Модальное окно создания */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            Создать запрос на увеличение зарплаты
          </Space>
        }
        open={isCreateModalOpen}
        onCancel={() => !createLoading && setIsCreateModalOpen(false)}
        footer={null}
        confirmLoading={createLoading}
      >
        <Form form={createForm} onFinish={handleCreateRequest} layout="vertical">
          <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: "6px" }}>
            <Space>
              <CalendarOutlined style={{ color: "#52c41a" }} />
              <Text>
                Запрос будет создан для текущего месяца: <Text strong>{dayjs().format("MM.YYYY")}</Text>
              </Text>
            </Space>
          </div>
          
          <Form.Item name="notes" label="Примечания">
            <TextArea rows={3} placeholder="Введите примечания к запросу (необязательно)" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                loading={createLoading}
              >
                Создать запрос
              </Button>
              <Button 
                onClick={() => setIsCreateModalOpen(false)}
                disabled={createLoading}
              >
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно просмотра */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Запрос на увеличение зарплаты за {selectedRequest?.month}
          </Space>
        }
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        width={900}
        footer={null}
        confirmLoading={Object.values(editLoading).some(Boolean)}
      >
        {selectedRequest && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: "24px" }}>
              <Descriptions.Item label="Месяц">
                <Space>
                  <CalendarOutlined />
                  {selectedRequest.month}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Статус">
                <Badge 
                  status={getStatusColor(selectedRequest.status) as any} 
                  text={getStatusText(selectedRequest.status)}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Общая сумма (USD)">
                <Space>
                  <DollarOutlined style={{ color: "#52c41a" }} />
                  <Text strong>{selectedRequest.totalRequestedAmount.USD.toLocaleString()}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Общая сумма (UZS)">
                <Space>
                  <DollarOutlined style={{ color: "#1890ff" }} />
                  <Text strong>{selectedRequest.totalRequestedAmount.UZS.toLocaleString()}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Создан">
                <Space>
                  <UserOutlined />
                  {selectedRequest.createdBy?.username || "Неизвестно"}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Дата создания">
                {dayjs(selectedRequest.createdAt).format("DD.MM.YYYY HH:mm")}
              </Descriptions.Item>
              {selectedRequest.approvedBy && (
                <>
                  <Descriptions.Item label="Одобрен">
                    <Space>
                      <UserOutlined />
                      {selectedRequest.approvedBy.username}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Дата одобрения">
                    {dayjs(selectedRequest.approvedAt).format("DD.MM.YYYY HH:mm")}
                  </Descriptions.Item>
                  {selectedRequest.approvalNotes && (
                    <Descriptions.Item label="Примечания одобрения" span={2}>
                      <div style={{ 
                        padding: "8px 12px", 
                        backgroundColor: "#f6ffed", 
                        border: "1px solid #b7eb8f", 
                        borderRadius: "6px",
                        color: "#52c41a"
                      }}>
                        {selectedRequest.approvalNotes}
                      </div>
                    </Descriptions.Item>
                  )}
                </>
              )}
              {selectedRequest.rejectedBy && (
                <>
                  <Descriptions.Item label="Отклонен">
                    <Space>
                      <UserOutlined />
                      {selectedRequest.rejectedBy.username}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Дата отклонения">
                    {dayjs(selectedRequest.rejectedAt).format("DD.MM.YYYY HH:mm")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Причина отклонения" span={2}>
                    <div style={{ 
                      padding: "8px 12px", 
                      backgroundColor: "#fff2f0", 
                      border: "1px solid #ffccc7", 
                      borderRadius: "6px",
                      color: "#ff4d4f"
                    }}>
                      {selectedRequest.rejectionReason}
                    </div>
                  </Descriptions.Item>
                </>
              )}
              {selectedRequest.notes && (
                <Descriptions.Item label="Примечания" span={2}>
                  <div style={{ 
                    padding: "8px 12px", 
                    backgroundColor: "#f0f9ff", 
                    border: "1px solid #91d5ff", 
                    borderRadius: "6px",
                    color: "#1890ff"
                  }}>
                    {selectedRequest.notes}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>
              <Space>
                <UserOutlined />
                Работники ({selectedRequest.items.length})
              </Space>
            </Divider>
            
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {selectedRequest.items.map((item, index) => 
                renderEditItem(item, index, selectedRequest._id)
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Модальное окно одобрения */}
      <Modal
        title={
          <Space>
            <CheckOutlined style={{ color: "green" }} />
            Одобрить запрос
          </Space>
        }
        open={isApproveModalOpen}
        onCancel={() => !approveLoading && setIsApproveModalOpen(false)}
        footer={null}
        confirmLoading={approveLoading}
      >
        <Form form={approveForm} onFinish={handleApproveRequest} layout="vertical">
          <Paragraph>
            Вы уверены, что хотите одобрить запрос на увеличение зарплаты за {selectedRequest?.month}?
            Это действие увеличит зарплаты всех работников согласно запросу.
          </Paragraph>
          
          <Form.Item name="notes" label="Примечания">
            <TextArea rows={3} />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<CheckOutlined />}
                loading={approveLoading}
              >
                Одобрить
              </Button>
              <Button 
                onClick={() => setIsApproveModalOpen(false)}
                disabled={approveLoading}
              >
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно отклонения */}
      <Modal
        title={
          <Space>
            <CloseOutlined style={{ color: "red" }} />
            Отклонить запрос
          </Space>
        }
        open={isRejectModalOpen}
        onCancel={() => !rejectLoading && setIsRejectModalOpen(false)}
        footer={null}
        confirmLoading={rejectLoading}
      >
        <Form form={rejectForm} onFinish={handleRejectRequest} layout="vertical">
          <Paragraph>
            Вы уверены, что хотите отклонить запрос на увеличение зарплаты за {selectedRequest?.month}?
          </Paragraph>
          
          <Form.Item
            name="rejectionReason"
            label="Причина отклонения"
            rules={[{ required: true, message: "Укажите причину отклонения" }]}
          >
            <TextArea rows={3} />
          </Form.Item>
          
          <Form.Item name="notes" label="Примечания">
            <TextArea rows={3} />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                danger 
                htmlType="submit" 
                icon={<CloseOutlined />}
                loading={rejectLoading}
              >
                Отклонить
              </Button>
              <Button 
                onClick={() => setIsRejectModalOpen(false)}
                disabled={rejectLoading}
              >
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
} 